// roleMap.ts — TYMCZASOWY most między rolami istniejących backendów a uprawnieniami Control Center.
//
// Dlaczego to jest tymczasowe (i ma tak zostać opisane, dopóki nie zniknie):
// TuSzama ma dziś kilka niezależnych systemów tożsamości, a role są ciągami znaków zaszytymi
// w kodzie i w zmiennych środowiskowych. Docelowy model (ADR-004) to jedno źródło tożsamości
// i uprawnienia nadawane per konto. Do tego czasu tłumaczymy role na uprawnienia TUTAJ,
// w jednym pliku, żeby migracja była jedną zmianą, a nie polowaniem po całej aplikacji.
//
// ------------------------------------------------------------------------------------------
//  PUŁAPKA NAZEWNICZA — DELIVER (R5-15 §20)
//  W systemie głównym TuSzamy rola `DELIVER` oznacza RESTAURACJĘ (podmiot, który wydaje/dowozi
//  posiłek), a NIE kierowcę. Kierowcy mają własny system (Drive) i własne tokeny.
//  Ta myląca nazwa NIE jest przenoszona do nowego modelu uprawnień — mapujemy ją jawnie
//  na uprawnienia restauracji i zostawiamy ten komentarz jako ostrzeżenie.
// ------------------------------------------------------------------------------------------

import type { Permission } from './permissions';

/** Role, które realnie występują dziś w backendach TuSzamy. */
export type LegacyRole =
  | 'crm_admin' // CRM: pełny operator (middleware/auth.js)
  | 'notification_templates' // CRM: wąskie konto (szablony powiadomień)
  | 'drive_admin' // Drive: admin panelu kierowców
  | 'drive_subadmin' // Drive: admin ograniczony do miasta (city scope)
  | 'main_admin' // System główny: administrator
  | 'main_deliver'; // System główny: DELIVER == RESTAURACJA (patrz ostrzeżenie wyżej)

const MAP: Record<LegacyRole, readonly Permission[]> = {
  crm_admin: [
    'analytics.sales.read',
    'crm.campaigns.read',
    'crm.customers.read',
    'reports.read',
    'system.incidents.read',
  ],
  notification_templates: ['crm.campaigns.read'],
  // Odzwierciedla 1:1 mapowanie po stronie SERWERA (tuszama_kod/backend/middleware/permissions.js).
  // Rozjazd między tymi dwiema listami oznacza, że front pokazuje coś, czego backend nie wyda —
  // albo, groźniej, chowa coś, co backend i tak by wydał.
  drive_admin: ['analytics.delivery.read', 'drive.routes.read', 'drive.deliveries.read', 'reports.read'],
  drive_subadmin: ['analytics.delivery.read', 'drive.deliveries.read'],
  main_admin: [
    'analytics.sales.read',
    'analytics.delivery.read',
    'admin.users.read',
    'restaurant.menu.read',
    'restaurant.sales.read',
    // Rozliczenia sieci — wyłącznie administrator/właściciel. `main_deliver` (restauracja) ani
    // `drive_subadmin` (miasto) tego NIE dostają; serwer i tak odmawia im 403.
    'settlements.read',
    // Grafik: odczyt i edycja dni par szkoła × restauracja — wyłącznie administrator/właściciel.
    'schedule.read',
    'schedule.write',
    // Administracja: konta, szkoły restauracji, moderacja menu — wyłącznie administrator/właściciel.
    // Żadna inna rola tego nie dostaje; serwer CC i tak odmawia 403 (`bramaCrm` na `/api/cc/admin/`).
    'users.read',
    'users.wallet.write',
    'users.delete',
    'restaurantSchools.read',
    'restaurantSchools.write',
    'menuModeration.read',
    'menuModeration.write',
    'reports.read',
    'system.incidents.read',
    'system.audit.read',
    // Skórki i pasek żyją w systemie głównym — administruje nimi jego administrator.
    'app.content.read',
    'app.content.write',
    'app.content.publish',
  ],
  main_deliver: ['restaurant.menu.read', 'restaurant.sales.read', 'analytics.delivery.read'],
};

export function permissionsForRoles(roles: readonly string[]): Permission[] {
  const out = new Set<Permission>();
  for (const role of roles) {
    const mapped = MAP[role as LegacyRole];
    if (!mapped) continue; // nieznana rola nie daje NICZEGO — fail-closed
    for (const p of mapped) out.add(p);
  }
  return [...out];
}

export function isKnownRole(role: string): role is LegacyRole {
  return role in MAP;
}
